import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FeedbackSubmitResponse } from "@/api/feedback-api"

import { Check, Copy, Download, ShieldAlert } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

type ConfirmationStepProps = {
  result: FeedbackSubmitResponse
  fullPayload: unknown
  onClose: () => void
}

export function ConfirmationStep({ result, fullPayload, onClose }: ConfirmationStepProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const reportText = JSON.stringify({ result, payload: fullPayload }, null, 2)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(reportText)
      setCopied(true)
      toast.success(t("feedback.confirmation.copied", "Report copied to clipboard"))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(
        t("feedback.confirmation.copyFailed", "Could not copy report — please download it instead.")
      )
    }
  }, [reportText, t])

  const handleDownload = useCallback(() => {
    const blob = new Blob([reportText], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = buildDownloadFilename(result.lane)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(t("feedback.confirmation.downloaded", "Report downloaded"))
  }, [reportText, result.lane, t])

  const escalated = result.escalated
  const emailSent = result.email_sent
  const emailError = result.email_error

  return (
    <div className="space-y-4">
      {escalated ? (
        <div className="border-primary/30 bg-primary/5 rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-primary mt-0.5 size-5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {emailSent
                  ? t("feedback.confirmation.escalatedTitle", "Sent to support")
                  : t("feedback.confirmation.escalatedFailTitle", "Escalation failed")}
              </p>
              <p className="text-muted-foreground text-xs">
                {emailSent
                  ? t(
                      "feedback.confirmation.escalatedDescription",
                      "A support engineer will follow up with you. You can copy or download the report below for your records."
                    )
                  : (emailError ??
                    t(
                      "feedback.confirmation.escalatedFailDescription",
                      "Email could not be delivered."
                    ))}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={cn(
            "animate-in fade-in-0 zoom-in-95 rounded-lg p-4 duration-300",
            STATUS_TONES.success.soft
          )}
        >
          <div className="flex items-start gap-3">
            <Check className="check-animated text-success mt-0.5 size-5 shrink-0" />
            <div className="space-y-1">
              <p className="text-foreground text-sm font-medium">
                {t("feedback.confirmation.thanksTitle", "Thanks for the feedback")}
              </p>
              <p className="text-foreground text-xs">
                {t(
                  "feedback.confirmation.thanksDescription",
                  "Your report was recorded for the product team. No reply is expected, but every report helps."
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-muted/30 rounded-md border p-3 text-xs">
        <span className="text-muted-foreground">
          {t("feedback.confirmation.referenceLabel", "Reference")}:{" "}
        </span>
        <span className="font-mono break-all">{result.feedback_id}</span>
      </div>

      <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-between">
        <div className="flex gap-2">
          <Button className="gap-1.5" size="sm" variant="outline" onClick={handleCopy}>
            {copied ? <Check className="text-success size-4" /> : <Copy className="size-4" />}
            {copied
              ? t("feedback.confirmation.copyDone", "Copied")
              : t("feedback.confirmation.copy", "Copy report")}
          </Button>
          <Button className="gap-1.5" size="sm" variant="outline" onClick={handleDownload}>
            <Download className="size-4" />
            {t("feedback.confirmation.download", "Download")}
          </Button>
        </div>
        <Button onClick={onClose}>{t("common.close", "Close")}</Button>
      </div>
    </div>
  )
}

/**
 * Give the downloaded JSON a filename that tells a support engineer what
 * they're looking at — no raw UUIDs — and sorts naturally by time.
 *
 *   HelloAI_feedback_2026-04-17T11-48-43.json      (response feedback)
 *   HelloAI_bugreport_2026-04-17T11-48-43.json     (bug)
 *   HelloAI_incidentreport_2026-04-17T11-48-43.json (incident)
 *   HelloAI_report_2026-04-17T11-48-43.json        (unknown lane; safe fallback)
 */
function buildDownloadFilename(lane: string | null | undefined): string {
  const kind = laneToFilenameKind(lane)
  // Local time in a filesystem-safe shape: 2026-04-17T11-48-43
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`
  return `HelloAI_${kind}_${stamp}.json`
}

function laneToFilenameKind(lane: string | null | undefined): string {
  switch (lane) {
    case "bug":
      return "bugreport"
    case "incident":
      return "incidentreport"
    case "response_feedback":
      return "feedback"
    default:
      return "report"
  }
}
